import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const services = [
  {
    id: 'isnetworld',
    platform: 'ISNetworld® · RAVS Plus',
    title: 'ISNetworld® Compliance Management',
    body: 'ISNetworld® is the leading contractor management platform used by major energy operators. Energy Contractor Services handles every aspect of your ISNetworld account — from initial setup to ongoing maintenance and score optimization. We ensure your grades stay high so you never miss a contract opportunity.',
    included: [
      'New account setup with quick turnaround',
      'Existing account score and grade improvement',
      'RAVS Plus documentation and support',
      'Ongoing month-to-month maintenance',
      'Injury rate documentation (TRIR, DART)',
      'Safety training record management',
    ],
    dark: true,
  },
  {
    id: 'veriforce',
    platform: 'Veriforce',
    title: 'Veriforce Compliance Management',
    body: 'Veriforce is a critical prequalification platform for energy contractors. Our team manages every component of your Veriforce compliance profile — ensuring your documentation is current, your scores are optimized, and your account stays in good standing with the operators who require it.',
    included: [
      'Veriforce account creation and setup',
      'Documentation management and uploads',
      'Compliance status monitoring',
      'Score and profile optimization',
      'Ongoing maintenance and updates',
      'Timely response to operator requirements',
    ],
    dark: false,
  },
  {
    id: 'avetta',
    platform: 'Avetta',
    title: 'Avetta Qualification Management',
    body: "Avetta connects contractors with top-tier energy operators. Navigating Avetta's qualification process requires expertise and attention to detail. ECS handles the full Avetta qualification cycle — getting you approved quickly and maintaining your status as operator requirements evolve.",
    included: [
      'Avetta account setup and qualification',
      'Document submission and verification',
      'Ongoing compliance maintenance',
      'Approval status management',
      'Operator-specific requirement tracking',
      'Renewal and update management',
    ],
    dark: true,
  },
  {
    id: 'ravs',
    platform: 'ISNetworld® RAVS Plus',
    title: 'RAVS Plus Support',
    body: 'RAVS Plus is one of the most demanding compliance requirements within ISNetworld. It requires specialized expertise to complete correctly. ECS provides dedicated RAVS Plus support, ensuring your documentation meets the strictest operator standards and your account maintains full compliance.',
    included: [
      'RAVS Plus documentation preparation',
      'Review and gap analysis',
      'Submission management',
      'Ongoing RAVS maintenance',
      'Operator-specific RAVS requirements',
      'Error correction and resubmission',
    ],
    dark: false,
  },
  {
    id: 'training',
    platform: 'Safety Training',
    title: 'Safety Training Programs',
    body: 'Comprehensive safety training programs designed for energy contractors. We help you find the best training solution for your workforce and ensure all training records are properly documented in your compliance accounts. Onsite, online, consultant-led, career tech, tailgate, and classroom options available.',
    included: [
      'Onsite safety training coordination',
      'Online training program access',
      'Classroom and career tech options',
      'Tailgate training sessions',
      'Training record documentation',
      'Compliance platform integration',
    ],
    dark: true,
  },
  {
    id: 'injury',
    platform: 'TRIR · DART · EMR',
    title: 'Injury Rate & Workers\' Comp Reduction',
    body: "High injury rates (TRIR, DART) and Experience Modification Rates (EMR) can disqualify contractors from working with major energy operators. ECS helps you reduce these numbers through targeted programs — protecting your ability to win contracts and reducing your Workers' Compensation insurance costs.",
    included: [
      'TRIR reduction strategy',
      'DART improvement programs',
      'EMR analysis and reduction',
      "Workers' Compensation savings",
      'Incident rate documentation',
      'Ongoing rate monitoring',
    ],
    dark: false,
  },
];

function ServiceDetailCard({
  svc,
  idx,
}: {
  svc: (typeof services)[0];
  idx: number;
}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 fade-up ${isVisible ? 'visible' : ''} ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
      style={{ transitionDelay: '0.05s' }}
    >
      {/* Dark panel */}
      <div
        className={`${svc.dark ? 'bg-[#0A0A0A]' : 'bg-[#111111]'} p-10 lg:p-14 flex flex-col justify-between min-h-72 ${idx % 2 === 1 ? 'lg:col-start-2' : ''}`}
      >
        <div className="w-8 h-0.5 bg-[#C9A030] mb-7" />
        <div className="flex-1">
          <p className="text-[#C9A030] text-[10px] tracking-[0.3em] uppercase font-semibold mb-3">
            {svc.platform}
          </p>
          <h3 className="font-serif text-3xl lg:text-[38px] font-light text-[#F2EDE3] leading-tight mb-4">
            {svc.title}
          </h3>
        </div>
        <a
          href="tel:4054081117"
          className="inline-flex items-center gap-2 text-[#C9A030] text-[10px] tracking-[0.22em] uppercase font-semibold cursor-pointer hover:text-[#D4B045] transition-colors whitespace-nowrap mt-8"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-xs" />
          </span>
          Call 405-408-1117
        </a>
      </div>

      {/* Light content panel */}
      <div className={`bg-[#F5F0E8] p-10 lg:p-14 ${idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
        <p className="text-[#5A5A5A] text-[15px] font-light leading-relaxed mb-8">
          {svc.body}
        </p>
        <p className="text-[#1A1409] text-[10px] tracking-[0.28em] uppercase font-semibold mb-5">
          What&rsquo;s Included:
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {svc.included.map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-[#C9A030] text-sm" />
              </span>
              <span className="text-[#5A5A5A] text-sm font-light leading-snug">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function ServicesDetailOverview() {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#F5F0E8]">
      {/* Section intro */}
      <div
        ref={ref}
        className={`px-6 lg:px-14 py-20 max-w-3xl fade-up ${isVisible ? 'visible' : ''}`}
      >
        <p className="overline mb-4">Our Services</p>
        <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#1A1409] leading-tight gold-line">
          Six Compliance Solutions.<br />One Dedicated Expert.
        </h2>
        <p className="text-[#5A5A5A] text-base font-light mt-8 leading-relaxed max-w-xl">
          Every service is handled by the same specialist who knows your business — not a rotating team. No contracts. Month-to-month. 100% guaranteed.
        </p>
      </div>

      {/* Service cards */}
      <div>
        {services.map((svc, idx) => (
          <ServiceDetailCard key={svc.id} svc={svc} idx={idx} />
        ))}
      </div>

      {/* Mid-page CTA */}
      <div className="bg-[#C9A030] px-6 lg:px-14 py-14">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl lg:text-3xl font-light text-[#0A0A0A] leading-snug">
              Ready to achieve 100% compliance — guaranteed?
            </p>
            <p className="text-[#0A0A0A]/65 text-sm font-light mt-1">
              No contracts. Month-to-month. Cancel anytime.
            </p>
          </div>
          <Link
            to="/contact"
            className="bg-[#0A0A0A] text-[#F2EDE3] px-8 py-4 text-[11px] font-bold tracking-[0.22em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-[#1A1A1A] flex-shrink-0"
          >
            Contact ECS Now
          </Link>
        </div>
      </div>
    </section>
  );
}
